import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatShortcut } from '@/components/ui';
import { UserMenu } from '@/components/UserMenu';
import { CommandPalette } from '@/components/shell/CommandPalette';
import { NotificationPopover } from '@/components/shell/NotificationPopover';
import { useCommandPalette } from '@/components/shell/useCommandPalette';

/**
 * EAA-1778: shared top chrome for the authenticated portal (launcher, profile,
 * org pages).
 *
 * Presentation only — reads identity from the auth context (D10: name/email/sub
 * from the token, nothing more) and links to the launcher and profile. Optional
 * `logoUrl` / `title` let the org-branding hook (WP-7) swap the EMG mark for the
 * acting org's identity; both default to EMG so an org without branding renders
 * unchanged.
 *
 * EAA-1813 (canonical shell — handoff frame 09): the console shell no longer
 * renders this component at all — it has its own in-panel `ConsoleTopbar`
 * (see `src/components/shell/ConsoleTopbar.tsx` and `ConsoleLayout.tsx`),
 * since frame 09's topbar sits inside the rounded content panel and carries
 * no brand/logo. This file is now launcher/profile-only, so the `rightSlot`
 * prop the console used to fill (EAA-1797) is dead and removed here.
 *
 * EAA-1813 (frames 13 + 14): search and notifications are LIVE now. Both used
 * to render `disabled` with an sr-only "chưa khả dụng" note purely because no
 * endpoint existed; EAA-1831 added `/api/v1/search` and
 * `/api/v1/me/notifications*`, so the real ⌘K palette and notification popover
 * replace them, and the `notificationCount` prop is gone — `NotificationPopover`
 * reads the count itself from `/me/notifications/summary` rather than having it
 * threaded down from callers (none ever passed it).
 */
export function AppBar({ logoUrl, title }: { logoUrl?: string; title?: string }) {
    const { t } = useTranslation();
    const palette = useCommandPalette();
    // EAA-1845: `title` used to default to the literal 'EMG Portal' in the
    // parameter list, which can't reach `t()` — resolved here instead so an
    // uncustomised org still gets a translated brand title.
    const displayTitle = title ?? t('shell.brand.defaultTitle');

    return (
        <header className="portal-appbar">
            <Link to="/" className="portal-appbar-brand">
                {logoUrl ? (
                    <img className="portal-appbar-logo" src={logoUrl} alt="" />
                ) : (
                    <span className="portal-appbar-mark" aria-hidden="true">
                        {t('shell.brand.mark')}
                    </span>
                )}
                <span>{displayTitle}</span>
            </Link>
            <span className="portal-appbar-spacer" />
            {/* EAA-1813: the search box is a `<button>` that opens the ⌘K dialog,
                not an `<input>` — the palette owns the real query field (frame 13),
                so typing happens there. It keeps the `.portal-appbar-search` box
                styling; only the button reset and the pointer cursor come from
                CommandPalette.css. */}
            <button
                type="button"
                className="portal-appbar-search portal-cmdk-trigger"
                onClick={palette.openPalette}
                aria-haspopup="dialog"
                aria-keyshortcuts="Meta+K Control+K"
                aria-label={t('shell.search.ariaLabel')}
            >
                <SearchIcon />
                <span className="portal-cmdk-trigger-label">{t('shell.search.triggerLabel')}</span>
                {/* EAA-1826: resolved per platform, never the literal ⌘K — on
                    Windows and Linux that named a key the keyboard does not
                    have. `aria-keyshortcuts` above already advertises both
                    chords, and `useCommandPalette` already accepts both; this
                    was the only half that lied. */}
                <span className="portal-appbar-search-kbd" aria-hidden="true">
                    {formatShortcut('K')}
                </span>
            </button>
            <NotificationPopover triggerClassName="portal-appbar-bell" iconSize={16} />
            {/* EAA-1804: the identity chip is a dropdown now, not a bare profile
                link — it carries the only logout reachable from the console. */}
            <UserMenu />
            {palette.open && <CommandPalette onClose={palette.closePalette} />}
        </header>
    );
}

/* Inline icons — no icon dependency (design-pass rule), matching the console
   nav's existing inline-SVG convention. */
function SearchIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
        </svg>
    );
}
